import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAccount, useConfig } from "wagmi";
import {
  getPublicClient,
  getWalletClient,
  waitForTransactionReceipt,
} from "wagmi/actions";
import { encodeFunctionData, erc20Abi, formatUnits, parseEventLogs, parseUnits, zeroAddress, type Address } from "viem";
import { keelEntradaAbi, keelVaultAbi, keelVaultFactoryAbi } from "../abi";
import type { Pool } from "./usePools";
import type { RedKeel } from "../config/networks";
import { esEstable, fraccionASwapear } from "../lib/precio";

export type Paso = { etiqueta: string; estado: "espera" | "haciendo" | "hecho" | "fallo" };

export type Peticion = {
  pool: Pool;
  /** cuanto se invierte, en unidades humanas del token que se deposita */
  cantidad: string;
  /** bolsa de gas, ADICIONAL a la inversion, en el mismo token */
  gas: string;
  /** semianchos del rango, en ticks, ya calculados por la pantalla. En modo "±%" (simétrico) los dos
   *  valen lo mismo; en "precios exactos" pueden diferir, y entonces se abre con `openAsym` para que
   *  el rango que escribió el dueño se respete tal cual, sin re-centrarlo. */
  below: number;
  above: number;
  spacing: number;
  /** se deposita el token0 del par (si no, el token1) */
  depositaToken0: boolean;
  /** % del deposito que se aparta como reserva DCA. Solo cuenta si se deposita la referencia. */
  reservaPct: number;
  /** 0 = nunca · 1 = en cada reajuste · 2 = por cada caida */
  modoReinyeccion: number;
  /** % de la reserva ORIGINAL por inyeccion */
  reinyeccionPct: number;
  /** cada cuanto % de caida se suelta un tramo (solo modo 2) */
  caidaPct: number;
  /** las ganancias vuelven a la posicion en vez de apartarse */
  reinvertir: boolean;
  /**
   * RESCATE: capital a recuperar, en unidades HUMANAS del estable. Solo lo pone el modal de
   * posiciones desvalorizadas, y cambia dos cosas del flujo:
   *
   *   · se abre con `openRecovery` en vez de `open`/`openAsym`, así que el rango no es el ±% que
   *     elige el dueño sino la BANDA que devuelve este importe cuando el precio vuelva;
   *   · NO hay swap de equilibrado. El caso de uso es justo el contrario: se trae el token volátil
   *     barato y venderlo ahora es consagrar la pérdida que se viene a recuperar.
   */
  recuperar?: string;
  /**
   * RESCATE: bolsa de gas EN EL ESTABLE, en vez de en el token que se trae. Quien llega con una
   * posición que se salió de rango trae el volátil, y gastar parte de ese volátil en gas es venderlo
   * justo cuando se viene a NO venderlo. Con esto la bolsa se deposita aparte, en el estable.
   */
  gasEnEstable?: string;
  /**
   * RESCATE: capital en el ESTABLE que también se trae. No todo el mundo sale de una pool desviada con
   * un solo token, y el estable no se queda mirando: llena el lado de ABAJO de la banda, comprando
   * más volátil si el precio sigue cayendo — el promediado pasivo que el contrato ya sabe hacer
   * (`RangeMath.recoveryFundedFloor/Ceil`), sin swap y sin deslizamiento.
   */
  estableExtra?: string;
  /**
   * RESCATE de quien YA VENDIÓ: llega solo con estable, así que no tiene nada que se revalorice y
   * ningún borde por arriba le devolvería el objetivo. La apertura compra el activo con lo que trae
   * y después dimensiona la banda. Es volver a tomar exposición, y la pantalla lo dice antes de
   * firmar — aquí solo se transporta la decisión.
   */
  comprarActivo?: boolean;
};

const POOL_ABI = [
  // slot0 truncado a (sqrtPriceX96, tick): Slipstream devuelve 6 campos, Uniswap 7; con 2 vale para ambos
  {
    type: "function",
    name: "slot0",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint160" }, { type: "int24" }],
  },
  {
    type: "function",
    name: "observe",
    stateMutability: "view",
    inputs: [{ type: "uint32[]" }],
    outputs: [{ type: "int56[]" }, { type: "uint160[]" }],
  },
  // La liquidez ACTIVA del tramo en el que esta el precio. Con ella se sabe cuanto va a mover el
  // precio el swap de apertura, y por tanto que deslizamiento hace falta para que no reviente.
  {
    type: "function",
    name: "liquidity",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint128" }],
  },
] as const;

/**
 * Crear piscina: createVault -> approve -> deposit -> open.
 *
 * CADA PISCINA ES UN VAULT NUEVO, como en los gestores de liquidez de referencia — pero solo si la factory de la red lo admite
 * (`MULTI_VAULT`). Las factories anteriores reservan la pool con `vaultFor[dueño][pool]` y revierten
 * con `AlreadyHasVault()` para siempre, asi que ahi se sigue reutilizando el vault vacio. Las dos
 * ramas conviven a proposito: mientras una red no tenga factory nueva, su unico camino es el viejo.
 *
 * La diferencia entre ambas no es cosmetica. Un vault REUTILIZADO conserva de la vez anterior la
 * cuota, el operador, el `paused`, la reinyeccion y la reinversion — `withdraw()` vacia el dinero,
 * no los ajustes — asi que la piscina "nueva" nace con la configuracion de la vieja y hay que ir
 * deshaciendola ajuste a ajuste antes de abrir. Uno solo que se olvide no falla a la vista: un vault
 * que quedo pausado se reabre sin protestar y el robot no lo toca nunca. Un vault NUEVO no tiene
 * nada de eso: nace a cero y solo se le pone lo que el usuario acaba de pedir.
 *
 * Las comprobaciones de antes de firmar NO son adorno, cada una viene de un fallo real:
 *
 *  · `vaultFor` primero, en la rama vieja. Muchos RPC devuelven el `AlreadyHasVault()` pelado
 *    ("execution reverted"), asi que el usuario veia un error que no tenia nada que ver con lo que
 *    hizo.
 *  · El saldo, EN WEI. Sin esto se crea el vault y se gasta el approve, y el deposit revierte
 *    dejando un vault vacio y dos transacciones pagadas. En coma flotante 1170 y 1169,999999
 *    parecen iguales; en wei, no.
 */
/**
 * Espera el recibo AGUANTANDO que el RPC se queje.
 *
 * Un flujo de cinco firmas no puede caerse porque el nodo tenga un mal momento. Y no es hipotético:
 * el 2026-08-16 `arbitrum-one.publicnode.com` contestó a `eth_getTransactionReceipt` con «Archive
 * requests require a personal token» sobre una transacción que se había minado perfectamente. El
 * vault quedó creado y la pantalla dijo que había fallado — el peor desenlace posible, porque
 * invita a repetir y pagar dos veces.
 *
 * La causa se arregló poniendo el RPC oficial por delante (ver `wagmi.ts`), pero la espera se
 * reintenta igual: el nodo que sirva mañana volverá a tener sus días, y aquí hay dinero firmado.
 *
 * @param queHacia para poder decir QUÉ paso se cayó, no un "algo falló" genérico.
 *
 * UN RECIBO REVERTIDO ES UN FALLO, Y HASTA EL 2026-08-17 AQUÍ NO LO ERA. `waitForTransactionReceipt`
 * devuelve el recibo tanto si la transacción salió bien como si se minó y revirtió: solo lanza si no
 * puede LEERLO. Sin mirar `status`, una transacción revertida se daba por buena y el flujo seguía al
 * paso siguiente.
 *
 * Y los pasos de aquí dependen unos de otros, así que el fallo no se quedaba quieto: pasó en
 * producción el 2026-08-17 — el `approve` no llegó a entrar, `deposit` revirtió por eso, y la
 * pantalla siguió hasta firmar `open` contra un vault VACÍO, que revirtió con `Insufficient()`.
 * Tres firmas, gas quemado en dos, y el usuario mirando un "execution reverted" sin causa a la
 * vista, con el dinero todavía en su wallet. El error real había ocurrido dos pasos antes.
 */
async function esperarRecibo(
  config: Parameters<typeof waitForTransactionReceipt>[0],
  hash: `0x${string}`,
  chainId: number,
  queHacia = "la transacción"
) {
  let ultimo: unknown;
  for (let intento = 0; intento < 4; intento++) {
    try {
      // SONDEO CADA 1 s, NO CADA 4. `waitForTransactionReceipt` usa el `pollingInterval` de viem,
      // que de fábrica son 4.000 ms. BNB Chain mina en ~1 s, así que con 4 s el recibo ya está en
      // cadena y la app sigue esperando hasta el siguiente sondeo. Crear una piscina son ~6
      // transacciones en serie, y esos 3 s perdidos por cada una eran ~18 s de rueda girando sobre
      // algo que ya había pasado — justo lo que hacía que «crear en BNB» se sintiera eterno. Las
      // otras tres redes minan aún más rápido, así que también ganan. Solo se sondea durante una
      // transacción pendiente, así que el coste en peticiones es acotado y breve.
      const recibo = await waitForTransactionReceipt(config, {
        hash,
        chainId: chainId as never,
        pollingInterval: 1000,
      });
      if (recibo.status === "reverted") {
        throw new Error(
          `${queHacia} entró en el bloque pero REVIRTIÓ (${hash.slice(0, 10)}…), así que no se hizo ` +
            `nada. El proceso se para aquí a propósito: seguir al paso siguiente solo gastaría más ` +
            `gas en otra transacción que también fallaría. Tu dinero sigue en tu wallet.`
        );
      }
      return recibo;
    } catch (e) {
      // Un revert no se reintenta: reintentar lo mismo da lo mismo, y esperar 20 s para repetirlo
      // solo alarga el tiempo que el usuario pasa sin saber qué pasó.
      if (e instanceof Error && e.message.includes("REVIRTIÓ")) throw e;
      ultimo = e;
      await new Promise((r) => setTimeout(r, 2000 * (intento + 1)));
    }
  }
  throw new Error(
    `La transacción se firmó y muy probablemente entró (${hash.slice(0, 10)}…), pero el nodo no me ` +
      `deja confirmarlo: ${ultimo instanceof Error ? ultimo.message.split("\n")[0] : ultimo}. ` +
      `NO la repitas sin comprobarla antes en el explorador.`
  );
}

/**
 * ¿Se puede usar `KeelEntrada` en esta red? La factory tiene que publicarla (`entrada()`) y ella
 * tiene que admitir la factory (`factoryOk`). Cualquier fallo de lectura = no (flujo de siempre).
 */
async function entradaLista(
  publico: NonNullable<ReturnType<typeof getPublicClient>>,
  factory: Address,
  entrada: Address
): Promise<boolean> {
  try {
    const [publicada, ok] = await Promise.all([
      publico.readContract({ address: factory, abi: keelVaultFactoryAbi, functionName: "entrada" }),
      publico.readContract({ address: entrada, abi: keelEntradaAbi, functionName: "factoryOk", args: [factory] }),
    ]);
    return ok && (publicada as Address).toLowerCase() === entrada.toLowerCase();
  } catch {
    return false;
  }
}

export function useCrear(red: RedKeel | undefined) {
  const { address } = useAccount();
  const config = useConfig();
  const qc = useQueryClient();
  const [pasos, setPasos] = useState<Paso[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [vaultCreado, setVaultCreado] = useState<Address | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const marcar = (i: number, estado: Paso["estado"]) =>
    setPasos((p) => p.map((x, j) => (j === i ? { ...x, estado } : x)));

  async function crear(pet: Peticion) {
    if (!address || !red) return;
    setError(null);
    setVaultCreado(null);
    setOcupado(true);

    // "Activar el robot" es `addQuota`. NO ES OPCIONAL: `rebalancesRemaining` nace en 0 —
    // `initialize` no lo toca y `open` tampoco— y `rebalance` revierte con `NoQuota()` mientras
    // valga cero. Un vault recien creado sin esto tiene el dinero dentro, la bolsa de gas llena y
    // el robot mirando: no lo reajusta NUNCA, y nada lo dice.
    // La primera etiqueta se decide MAS ABAJO, cuando ya sabemos si hay un vault vacio que
    // reutilizar. Se deja de sitio y se corrige antes de firmar nada.
    const etiquetas = ["Crear tu vault", "Aprobar", "Depositar", "Abrir posición", "Activar el robot"];
    // El vault nace con la reinyeccion apagada y sin reinversion: si el usuario las pidio hay que
    // llamarlas aparte, o la reserva se quedaria quieta para siempre sin que nada avisara.
    if (pet.modoReinyeccion !== 0) etiquetas.push("Programar reinyección");
    if (pet.reinvertir) etiquetas.push("Activar reinversión");
    setPasos(etiquetas.map((etiqueta) => ({ etiqueta, estado: "espera" as const })));

    const publico = getPublicClient(config, { chainId: red.idCadena });
    let paso = 0;
    try {
      if (!publico) throw new Error("sin cliente RPC");
      const wallet = await getWalletClient(config, { chainId: red.idCadena });

      const { pool, token0, token1 } = pet.pool;

      // RESCATE: SOLO SI LA IMPLEMENTACIÓN QUE VA A NACER TIENE `openRecovery`. El 2026-09-29, en
      // Robinhood (impl del 11-sep, sin rescate), la apertura acabó en un rango normal ±% que ignoró
      // el objetivo del dueño. Mejor parar aquí, antes de crear ni depositar nada.
      if (pet.recuperar) {
        const impl = (await publico.readContract({
          address: red.factory, abi: keelVaultFactoryAbi, functionName: "implementation",
        })) as Address;
        const codigo = (await publico.getCode({ address: impl })) ?? "0x";
        if (!codigo.toLowerCase().includes("6c3188bd")) {
          throw new Error(
            "El rescate todavía no está disponible en esta red: sus contratos no tienen la apertura con objetivo. No se ha creado ni depositado nada."
          );
        }
      }

      // ---- 1. ¿esta factory admite varios vaults por pool? Y si no, ¿ya hay uno aqui?
      //         Las dos preguntas, antes de abrir la wallet.
      const multi = await publico
        .readContract({ address: red.factory, abi: keelVaultFactoryAbi, functionName: "MULTI_VAULT" })
        .catch(() => false); // una factory vieja no tiene la funcion: ese revert significa "no"

      const ya = multi
        ? zeroAddress
        : await publico.readContract({
            address: red.factory,
            abi: keelVaultFactoryAbi,
            functionName: "vaultFor",
            args: [address, pool],
          });
      // DE AQUI PARA ABAJO, SOLO LA RAMA VIEJA. Con `multi` no se lee nada: se crea un vault limpio
      // y no hay ajustes heredados que deshacer.
      //
      // REUTILIZAR EN VEZ DE BLOQUEAR. En las factories anteriores `createVault` solo deja UN vault
      // por dueño y pool, y no hay setter que libere el hueco — de ahi la idea, extendida durante
      // semanas, de que sacar todo "quemaba" la pool para siempre. No es asi: lo que se agota es
      // crear OTRO vault, no volver a usar el que ya tienes. `withdraw()` deja `tokenId = 0` y
      // `costBasisRef = 0`, y `open()` solo exige `tokenId == 0`, asi que el vault vacio se reabre
      // tantas veces como haga falta, conservando sus ajustes, su operador y su cuota.
      //
      // Solo bloquea el vault VIVO, que es el caso real de "ya tienes una piscina aqui".
      let vault = ya as Address;
      let reutilizado = false;
      let cuotaYa = 0n;
      let modoYa = 0;
      let reinvYa = false;
      let pausadoYa = false;
      let operadorYa = zeroAddress as Address;
      let operadorHoy = zeroAddress as Address;
      if (ya !== zeroAddress) {
        const comun = { address: ya, abi: keelVaultAbi } as const;
        const [abiertoYa, cuota, modo, reinv, pausado, operador, porDefecto] = await publico.multicall({
          allowFailure: false,
          contracts: [
            { ...comun, functionName: "tokenId" },
            { ...comun, functionName: "rebalancesRemaining" },
            { ...comun, functionName: "reinjectMode" },
            { ...comun, functionName: "reinvestFees" },
            { ...comun, functionName: "paused" },
            { ...comun, functionName: "operator" },
            { address: red.factory, abi: keelVaultFactoryAbi, functionName: "defaultOperator" },
          ],
        });
        if (abiertoYa !== 0n) {
          throw new Error(
            `Ya tienes una piscina abierta en ${pet.pool.pair} (${ya.slice(0, 10)}…). En esta red solo puede haber una ` +
              `por pool: para meter más dinero, ábrela y usa «Añadir fondos», así todo trabaja en la misma posición.`
          );
        }
        reutilizado = true;
        cuotaYa = BigInt(cuota);
        modoYa = Number(modo);
        reinvYa = reinv;
        pausadoYa = pausado;
        operadorYa = operador as Address;
        operadorHoy = porDefecto as Address;
      }

      // UN VAULT REUTILIZADO NO NACE LIMPIO, y ahi esta la trampa: `withdraw()` vacia el dinero pero
      // NO toca los ajustes. Se conservan la cuota, el operador, las guardias, la reinyeccion y la
      // reinversion de la vez anterior.
      //
      // Eso es bueno para la cuota —no hay que volver a pagarla— y peligroso para lo demas: si la
      // vez pasada dejaste la reinyeccion en modo 2 y ahora creas SIN reserva, se quedaria
      // programada apuntando a una reserva que ya no existe. Por eso cada ajuste se compara con lo
      // que hay y solo se firma lo que de verdad cambia.
      // Y los dos que NO se veían y no fallan a la vista: `paused` y `operator` tambien sobreviven a
      // `withdraw()`, y `openPosition` no mira ninguno de los dos. O sea que un vault que se dejo
      // pausado —lo normal antes de salir— se REABRE sin protestar y la posicion queda a la
      // intemperie: el robot revierte con `Paused()` en cada intento y no rebalancea ni barre, sin
      // un solo error visible en la app. Igual si el operador guardado ya no es el que la factory
      // publica hoy: el keeper de ahora ni siquiera pasa el `NoOperator()`.
      const hazCuota = !reutilizado || cuotaYa < 100n;
      const hazReanudar = reutilizado && pausadoYa;
      const hazOperador =
        reutilizado && operadorHoy !== zeroAddress && operadorYa.toLowerCase() !== operadorHoy.toLowerCase();
      const hazReinyeccion = pet.modoReinyeccion !== modoYa || (pet.modoReinyeccion !== 0 && reutilizado);
      const hazReinvertir = pet.reinvertir !== reinvYa;

      etiquetas.length = 0;
      etiquetas.push(
        reutilizado ? "Reutilizar tu vault" : "Crear tu vault",
        "Aprobar", "Depositar", "Abrir posición",
      );
      if (hazCuota) etiquetas.push("Activar el robot");
      if (hazReanudar) etiquetas.push("Reanudar el vault");
      if (hazOperador) etiquetas.push("Reasignar el robot");
      if (hazReinyeccion) {
        etiquetas.push(pet.modoReinyeccion === 0 ? "Apagar la reinyección" : "Programar reinyección");
      }
      if (hazReinvertir) {
        etiquetas.push(pet.reinvertir ? "Activar reinversión" : "Apagar la reinversión");
      }
      setPasos(etiquetas.map((etiqueta) => ({ etiqueta, estado: "espera" as const })));

      // ---- 2. el token con el que se deposita. Por defecto el estable, pero se puede meter el
      //         volatil: quien ya tiene el activo no tiene por que venderlo antes.
      const estableEsToken0 = esEstable(token0.symbol) && !esEstable(token1.symbol);
      const depEsToken0 = pet.depositaToken0;
      const dep = depEsToken0 ? token0 : token1;

      const depWei = parseUnits(pet.cantidad, dep.decimals);
      const gasWei = parseUnits(pet.gas || "0", dep.decimals);
      const totalWei = depWei + gasWei; // lo que sale de la wallet: inversion + gas

      // LA RESERVA VIVE SIEMPRE EN EL TOKEN DE REFERENCIA (el estable del par). Si se esta
      // depositando el volatil no hay reserva que apartar: `open` la rechazaria o la apartaria del
      // token equivocado.
      const reservaWei =
        depEsToken0 === estableEsToken0 && pet.reservaPct > 0
          ? (depWei * BigInt(Math.round(pet.reservaPct * 100))) / 10000n
          : 0n;

      // ---- 3. saldo, en wei
      const saldo = await publico.readContract({
        address: dep.address,
        abi: erc20Abi,
        functionName: "balanceOf",
        args: [address],
      });
      if (totalWei > saldo) {
        const tienes = Number(saldo) / 10 ** dep.decimals;
        throw new Error(
          `Solo tienes ${tienes.toLocaleString("es", { maximumFractionDigits: 6 })} ${dep.symbol} y ` +
            `hacen falta ${pet.cantidad} de inversión más ${pet.gas || 0} de gas.`
        );
      }

      // ---- 4. el rango y el swap que pide
      const { below, above } = pet;
      const asimetrico = below !== above; // "precios exactos" con la banda descentrada → openAsym
      const [slot0, twap] = await Promise.all([
        publico.readContract({ address: pool, abi: POOL_ABI, functionName: "slot0" }),
        publico
          .readContract({ address: pool, abi: POOL_ABI, functionName: "observe", args: [[300, 0]] })
          .then(([tc]) => {
            const d = tc[1]! - tc[0]!;
            let a = Number(d / 300n);
            if (d < 0n && d % 300n !== 0n) a -= 1; // redondeo a -inf, como Uniswap
            return a;
          })
          .catch(() => null),
      ]);
      const centro = twap ?? slot0[1];
      // La proporcion se calcula sobre lo que DE VERDAD se va a desplegar: la reserva se queda
      // fuera de la posicion, asi que swapear sobre el deposito entero vende de mas.
      const desplegable = depWei - reservaWei;
      // EN UN RESCATE NO SE SWAPEA NADA. Se viene a recuperar una pérdida trayendo el token volátil
      // barato; venderlo ahora para formar el par es consagrar exactamente lo que se quiere evitar.
      // `openRecovery` abre con lo que haya y la banda hace el resto según el precio vuelva.
      const frac = pet.recuperar
        ? (pet.comprarActivo ? 1 : 0) // llega vendido: se compra el activo con todo lo desplegable
        : fraccionASwapear(slot0[0], centro, pet.spacing, below, above, depEsToken0);
      let swapWei = (desplegable * BigInt(Math.round(frac * 1e6))) / 1_000_000n;

      // ---- 4.b EL DESLIZAMIENTO SE MIDE CONTRA LA POOL, NO SE DEJA EN EL DE FABRICA.
      //
      // Un vault nace con `swapSlippageBps = 50` (0,5%), y eso vale en una pool profunda. En una
      // fina el swap de apertura mueve el precio el solo, y entonces `open` revierte con «Too
      // little received» — que es lo que ve el usuario: un error del router, sin pista de que la
      // culpa es de un parametro suyo. Y como cada intento crea un vault NUEVO (MULTI_VAULT), el
      // siguiente vuelve a nacer con 0,5% y falla igual: un bucle del que no se sale desde la app,
      // porque el detalle no deja abrir una posicion en un vault que ya existe.
      //
      // Medido en CASHCAT/USDG 1% el 2026-08-16: vender 446 USDG mueve el precio 0,99%, o sea un
      // deslizamiento medio del 0,49% — justo en el filo del 0,5%. Con el mismo capital en la pool
      // de WETH/USDG no se nota.
      //
      // Se calcula con la liquidez ACTIVA y la formula de Uniswap para el tramo en el que esta el
      // precio: vendiendo token1, sqrtP' = sqrtP + Δy·Q96/L; vendiendo token0, sqrtP' =
      // L·Q96·sqrtP / (L·Q96 + Δx·sqrtP). El coste medio del swap es ~la mitad del recorrido, y se
      // pide un 50% de margen encima por lo que se mueva el precio entre firmar y minar.
      // La desviacion maxima con la que NACE un vault (`KeelVault.initialize`). Es el peor caso de
      // brecha spot-TWAP que puede tener una apertura que llegue a ejecutarse: por encima de esto,
      // `_requireHonestPrice` la rechaza entera con `BadPrice` y no hay swap que valga.
      const devTope = 200;
      let bpsNecesarios = 0;
      if (swapWei > 0n) {
        const L = await publico.readContract({
          address: pool, abi: POOL_ABI, functionName: "liquidity",
        });
        if (L > 0n) {
          const Q96 = 2n ** 96n;
          const sq = slot0[0];

          // ---- TOPE DE IMPACTO DE ENTRADA (anti-slippage) --------------------------------------
          // El swap de apertura convierte ~la mitad del depósito al otro token; en una pool FINA eso
          // mueve el precio y se paga en slippage (el dueño perdió ~1,8% así el 2026-09-05). Aquí se
          // TOPA `swapWei` para que el swap de apertura mueva el precio como mucho `ENTRY_MAX_MOV`; lo
          // que no quepa se deposita SUELTO y el keeper lo despliega en ciclos, cada uno bajo su propio
          // tope de impacto (SWAP_MAX_IMPACT_BPS), dejando que el arbitraje reponga la pool entre swaps.
          // En pool PROFUNDA el swap completo ya mueve poco -> no se topa (abre entero, rápido). Es la
          // versión de entrada de la misma idea del keeper y de la guarda de salida.
          const ENTRY_MAX_MOV = 0.01; // 1% de movimiento de precio (~0,5% de pérdida media) por swap
          if (swapWei > 0n) {
            const sqTope = depEsToken0
              ? BigInt(Math.floor(Number(sq) * Math.sqrt(1 - ENTRY_MAX_MOV))) // vendo token0: precio baja
              : BigInt(Math.floor(Number(sq) * Math.sqrt(1 + ENTRY_MAX_MOV))); // vendo token1: precio sube
            const maxSwap = depEsToken0
              ? (L * Q96 * (sq - sqTope)) / (sqTope * sq)
              : (L * (sqTope - sq)) / Q96;
            if (maxSwap > 0n && maxSwap < swapWei) swapWei = maxSwap; // capa; el resto queda suelto
          }
          // -------------------------------------------------------------------------------------

          // depEsToken0 = vendemos token0
          const sqFin = depEsToken0
            ? (L * Q96 * sq) / (L * Q96 + swapWei * sq)
            : sq + (swapWei * Q96) / L;
          const r = Number(sqFin) / Number(sq);
          const mov = Math.abs(r * r - 1);          // cuanto se mueve el precio, en tanto por uno

          // Y SE SUMA LA DISTANCIA ENTRE EL SPOT Y EL TWAP, que no es un detalle: el minimo de
          // salida que exige `_guardedSwap` se calcula sobre el TWAP —a proposito, porque el TWAP
          // no se puede mover dentro de un bloque— pero el swap se ejecuta al SPOT. Si van
          // separados, esa diferencia se paga como si fuera deslizamiento. Contarla de menos es
          // exactamente el error que hace revertir un swap que en el papel cabia.
          //
          // SE USA EL PEOR CASO, NO LA BRECHA DE AHORA. Medir la brecha en este instante y ajustar
          // a esa medida es una carrera perdida: entre que el usuario firma y la transaccion se
          // mina pasan segundos, y en un memecoin la brecha se mueve en ese rato — medida en
          // CASHCAT/USDG el 2026-08-17, paso del 1,25% al 0,41% en unos minutos. Si crece despues
          // de calcularla, `open` vuelve a revertir y el usuario a mirar «Too little received».
          //
          // El peor caso lo pone el propio vault: `_requireHonestPrice` rechaza la operacion
          // ENTERA si el spot se separa del TWAP mas de `maxTwapDeviationTicks`, asi que ninguna
          // apertura que llegue a ejecutarse puede tener una brecha mayor que esa. Cubrirla toda
          // no cuesta dinero: el deslizamiento es un TOPE, no un peaje — el swap se ejecuta al
          // precio que haya, y lo unico que hace un tope holgado es no cancelar la operacion.
          const brechaMax = Math.abs(Math.pow(1.0001, devTope) - 1);

          bpsNecesarios = Math.ceil(((mov / 2) * 1.5 + brechaMax) * 10000) + 25;
        }
      }
      // El contrato topa el deslizamiento en 1000 (10%): mas que eso no es un ajuste, es aceptar
      // cualquier precio. Si hiciera falta mas, el problema es el tamaño y hay que avisar.
      if (bpsNecesarios > 1000) {
        throw new Error(
          `Con ${pet.cantidad} ${dep.symbol}, el swap de apertura movería el precio de esta pool un ` +
            `${(bpsNecesarios / 100).toFixed(1)}% — más de lo que ningún deslizamiento razonable ` +
            `cubre. Esta pool es demasiado fina para esa cantidad: prueba con menos, o con un rango ` +
            `más ancho (que necesita vender menos para entrar).`
        );
      }
      const ajustarSlippage = bpsNecesarios > 50;

      // Cadencia corta desde el primer minuto: ver 7.b.
      const NUEVO_COOLDOWN = 300; // 5 min, a la par del sondeo del keeper

      // LOS ARGUMENTOS DEL RESCATE, UNA SOLA VEZ, para la estimación Y para la firma. Antes la
      // estimación llevaba una lista vieja de 5 (la `openRecovery` de ahora pide 8): viem fallaba al
      // codificar, el `catch` lo tragaba y la wallet estimaba sola, sin el 50% de margen.
      const argsRecuperar = pet.recuperar
        ? ([
            estableEsToken0,        // cual de los dos es la referencia
            parseUnits(pet.recuperar, estableEsToken0 ? pet.pool.token0.decimals : pet.pool.token1.decimals),
            // Semiancho para el caso sin banda (llega vendido y el rango nace normal): la rejilla de
            // la pool como mínimo, y si no hay dato, un ancho sensato.
            pet.comprarActivo ? Math.max(pet.spacing, Math.round(300 / pet.spacing) * pet.spacing) : 0,
            depEsToken0,            // zeroForOne: si se deposita token0, se vende token0 para comprar el activo
            swapWei,
            0n,                     // sin reserva: todo el capital va a la banda de recuperacion
            // La bolsa puede ir en el token que se trae o en el estable: el vault la retiene del
            // lado que se le diga, y `_prepareOpen` comprueba que ese saldo esté dentro.
            pet.gasEnEstable && Number(pet.gasEnEstable) > 0
              ? (estableEsToken0 ? parseUnits(pet.gasEnEstable, pet.pool.token0.decimals) : 0n)
              : (depEsToken0 ? gasWei : 0n),
            pet.gasEnEstable && Number(pet.gasEnEstable) > 0
              ? (estableEsToken0 ? 0n : parseUnits(pet.gasEnEstable, pet.pool.token1.decimals))
              : (depEsToken0 ? 0n : gasWei),
          ] as const)
        : null;
      const argsOpen = argsRecuperar
        ? argsRecuperar
        : asimetrico
          ? ([depEsToken0, swapWei, below, above, estableEsToken0, reservaWei, depEsToken0 ? gasWei : 0n, depEsToken0 ? 0n : gasWei] as const)
          : ([depEsToken0, swapWei, below, estableEsToken0, reservaWei, depEsToken0 ? gasWei : 0n, depEsToken0 ? 0n : gasWei] as const);
      const fnOpen = pet.recuperar ? "openRecovery" : asimetrico ? "openAsym" : "open";

      // ---- 4.c ALTA EN UNA FIRMA, si la red tiene `KeelEntrada` y la factory la publica.
      //
      // El flujo de abajo aprueba al VAULT que acaba de nacer, y Rabby/MetaMask lo marcan como
      // "dirección EOA / nunca interactuado / confianza $0" (su indexador aún no lo ha visto). Con la
      // entrada se aprueba a UN contrato fijo, verificado y etiquetado, y una sola transacción crea el
      // vault A NOMBRE DEL USUARIO, deposita, ajusta, abre y carga la cuota — las mismas llamadas, en el
      // mismo orden y con los mismos importes que las firmas sueltas de abajo.
      //
      // Se comprueba en cadena antes de usarla: si la factory no la publica o ella no admite la
      // factory, se sigue por el flujo de siempre en vez de fallar.
      if (!reutilizado && red.entrada && (await entradaLista(publico, red.factory, red.entrada))) {
        const entrada = red.entrada;
        const est = estableEsToken0 ? token0 : token1;
        const estWei =
          parseUnits(pet.gasEnEstable || "0", est.decimals) + parseUnits(pet.estableExtra || "0", est.decimals);

        // Lo que sale de la wallet, por token (si el estable es el mismo token que se deposita, se suma).
        const aportes: { token: Address; amount: bigint; symbol: string }[] = [
          { token: dep.address, amount: totalWei, symbol: dep.symbol },
        ];
        if (estWei > 0n) {
          if (est.address.toLowerCase() === dep.address.toLowerCase()) aportes[0]!.amount += estWei;
          else aportes.push({ token: est.address, amount: estWei, symbol: est.symbol });
        }
        for (const a of aportes) {
          const s = await publico.readContract({
            address: a.token, abi: erc20Abi, functionName: "balanceOf", args: [address],
          });
          if (s < a.amount) {
            throw new Error(
              `Hacen falta ${formatUnits(a.amount, a.token === dep.address ? dep.decimals : est.decimals)} ${a.symbol} ` +
                `y tienes ${formatUnits(s, a.token === dep.address ? dep.decimals : est.decimals)}.`
            );
          }
        }

        // Las MISMAS llamadas que firmaría el usuario una a una (pasos 7 a 10), sobre un vault que
        // nace limpio: guardias de fábrica (twap 300 s, desviación 200 ticks, 0,5%, 1 h), colchón
        // 50 bps y `recoverMarginRef` 0 — ver `KeelVault.initialize`.
        const llamar = (functionName: string, args: readonly unknown[]) =>
          encodeFunctionData({ abi: keelVaultAbi, functionName, args } as never);
        const calls: `0x${string}`[] = [llamar("deposit", [dep.address, totalWei])];
        if (estWei > 0n) calls.push(llamar("deposit", [est.address, estWei]));
        calls.push(llamar("setGuards", [300, 200, ajustarSlippage ? bpsNecesarios : 50, NUEVO_COOLDOWN]));
        // Rescate: objetivo SIN colchón de fábrica (7.c). En un vault nuevo el bps vale 50 y el fijo 0.
        // SIN colchón en TODA apertura (decisión del dueño 2026-10-02): la recuperación apunta exactamente
        // al capital (`costBasisRef`, que ya incluye lo añadido y lo reinvertido).
        calls.push(llamar("setRecoverMargin", [0]));
        calls.push(llamar(fnOpen, argsOpen));
        calls.push(llamar("addQuota", [500]));
        if (pet.modoReinyeccion !== 0) {
          calls.push(llamar("setReinjection", [
            pet.modoReinyeccion, Math.round(pet.reinyeccionPct * 100), Math.round(pet.caidaPct * 100),
          ]));
        }
        if (pet.reinvertir) calls.push(llamar("setReinvest", [true]));

        const etiq = [
          ...aportes.map((a) => `Aprobar ${a.symbol} a Keel (contrato verificado)`),
          "Crear y abrir la piscina (1 firma)",
        ];
        setPasos(etiq.map((etiqueta) => ({ etiqueta, estado: "espera" as const })));

        // Permiso EXACTO a la entrada, por token. Nunca infinito.
        for (let i = 0; i < aportes.length; i++) {
          const a = aportes[i]!;
          paso = i;
          marcar(paso, "haciendo");
          const permitido = await publico.readContract({
            address: a.token, abi: erc20Abi, functionName: "allowance", args: [address, entrada],
          });
          if (permitido < a.amount) {
            const h = await wallet.writeContract({
              address: a.token, abi: erc20Abi, functionName: "approve", args: [entrada, a.amount],
            });
            await esperarRecibo(config, h, red.idCadena, "El permiso sobre tu " + a.symbol);
          }
          marcar(paso, "hecho");
        }

        paso = aportes.length;
        marcar(paso, "haciendo");
        const argsCrear = [
          red.factory, pool, aportes.map(({ token, amount }) => ({ token, amount })), calls,
        ] as const;
        // Estimación contra el estado REAL (los approve ya tienen recibo) y +50%, por lo mismo que en
        // el depósito y la apertura del flujo viejo: la wallet estima antes de tiempo y se queda corta.
        let gasCrear: bigint | undefined;
        try {
          const e = await publico.estimateContractGas({
            address: entrada, abi: keelEntradaAbi, functionName: "crear", args: argsCrear, account: address,
          });
          gasCrear = (e * 15n) / 10n;
        } catch { /* que estime la wallet */ }
        const hCrear = await wallet.writeContract({
          address: entrada, abi: keelEntradaAbi, functionName: "crear", args: argsCrear,
          ...(gasCrear ? { gas: gasCrear } : {}),
        });
        const recibo = await esperarRecibo(config, hCrear, red.idCadena, "La creación y apertura de la piscina");
        const nacido =
          parseEventLogs({ abi: keelVaultFactoryAbi, eventName: "VaultCreated", logs: recibo.logs })[0]?.args.vault ??
          parseEventLogs({ abi: keelEntradaAbi, eventName: "Creado", logs: recibo.logs })[0]?.args.vault;
        if (!nacido) throw new Error("La piscina se creó, pero el recibo no trae su dirección: búscala en «Mis piscinas».");
        marcar(paso, "hecho");

        setVaultCreado(nacido);
        qc.invalidateQueries({ queryKey: ["flota"] });
        qc.invalidateQueries({ queryKey: ["vaults"] });
        qc.invalidateQueries({ queryKey: ["cobrado"] });
        return;
      }

      // ---- 5. crear el vault, o quedarse con el que ya hay
      //
      // La direccion sale del EVENTO de su propio recibo, no de `vaultFor`. Con varios vaults por
      // pool, `vaultFor` es solo el ULTIMO creado: si el usuario tiene dos pestañas abiertas —o
      // vuelve a crear mientras esta espera— leerlo devolveria el vault de la otra, y el deposito
      // se iria a un contrato que no es el de esta pantalla. El recibo solo puede hablar de la
      // transaccion que acabamos de firmar.
      marcar(paso, "haciendo");
      if (!reutilizado) {
        const hCrear = await wallet.writeContract({
          address: red.factory, abi: keelVaultFactoryAbi, functionName: "createVault", args: [pool],
        });
        const recibo = await esperarRecibo(config, hCrear, red.idCadena, "La creación del vault");
        const creados = parseEventLogs({
          abi: keelVaultFactoryAbi, eventName: "VaultCreated", logs: recibo.logs,
        });
        vault = creados[0]?.args.vault ?? zeroAddress;
        // Respaldo por si un RPC devuelve el recibo sin logs, que pasa. Con `multi` sigue siendo
        // correcto: nadie mas ha creado en esta pool entre una linea y la otra salvo el propio
        // usuario, y en ese caso el error de arriba es preferible a un silencio.
        if (vault === zeroAddress) {
          vault = await publico.readContract({
            address: red.factory, abi: keelVaultFactoryAbi, functionName: "vaultFor", args: [address, pool],
          });
        }
        if (vault === zeroAddress) throw new Error("el vault no aparece tras crearlo");
      }
      marcar(paso, "hecho");

      // ---- 6. approve (solo si hace falta)
      //
      // El spender es EL VAULT DEL USUARIO, y Rabby lo marca como «Danger: the spender address is an
      // EOA, potentially a scam address». Es un FALSO POSITIVO suyo: el vault es un clon EIP-1167
      // que acaba de nacer en la transaccion anterior, y su indexador todavia no lo ha visto —
      // comprobado en cadena el 2026-08-17 con 0x02EDdE73…FaabC5, que tiene 45 bytes de codigo y
      // `owner()` = el usuario. El permiso tampoco es explotable: `deposit` es `onlyOwner` y hace
      // `transferFrom(msg.sender)`, y se aprueba el importe EXACTO, nunca infinito.
      paso = 1;
      marcar(paso, "haciendo");
      const permitido = await publico.readContract({
        address: dep.address, abi: erc20Abi, functionName: "allowance", args: [address, vault],
      });
      if (permitido < totalWei) {
        const hOk = await wallet.writeContract({
          address: dep.address, abi: erc20Abi, functionName: "approve", args: [vault, totalWei],
        });
        await esperarRecibo(config, hOk, red.idCadena, "El permiso sobre tu " + dep.symbol);
      }
      marcar(paso, "hecho");

      // ---- 7. depositar
      paso = 2;
      marcar(paso, "haciendo");
      // EL GAS SE ESTIMA AQUÍ, NO EN LA WALLET, Y CON MARGEN.
      //
      // La wallet calcula el límite del `deposit` cuando ENSEÑA la confirmación, y en ese momento el
      // `approve` de arriba puede no estar minado todavía: estima por el camino corto y manda un
      // límite que no da. Medido con USDT en Ethereum (2026-09-18): la wallet mandó 51.696 y el
      // depósito necesitaba 111.375 — dos transacciones perdidas, y con el error más inútil que hay,
      // «execution reverted for an unknown reason», porque quedarse sin gas dentro de una llamada
      // interna se ve igual que un revert.
      //
      // Aquí se estima contra el estado REAL (el approve ya tiene recibo) y se añade un 50%: los
      // tokens viejos como USDT cuestan el doble que un ERC-20 normal, y más aún la primera vez que
      // tocan una dirección, porque estrenar el hueco de su saldo son 20.000 de gas extra. Lo que no
      // se gasta no se cobra, así que pasarse no tiene coste; quedarse corto cuesta la transacción.
      let gasDep: bigint | undefined;
      try {
        const est = await publico.estimateContractGas({
          address: vault, abi: keelVaultAbi, functionName: "deposit", args: [dep.address, totalWei],
          account: address,
        });
        gasDep = (est * 15n) / 10n;
      } catch { /* si el nodo no deja estimar, que lo haga la wallet como antes */ }

      const hDep = await wallet.writeContract({
        address: vault, abi: keelVaultAbi, functionName: "deposit", args: [dep.address, totalWei],
        ...(gasDep ? { gas: gasDep } : {}),
      });
      const recDep = await esperarRecibo(config, hDep, red.idCadena, "El depósito");
      marcar(paso, "hecho");

      // EL DINERO TIENE QUE ESTAR DENTRO ANTES DE ABRIR, Y SE COMPRUEBA LEYENDO EL VAULT.
      //
      // No es desconfiar del paso anterior: es que `open` es la unica firma de las cuatro que no
      // puede fallar barato. Si el vault no tiene el saldo, revierte con `Insufficient()` —un error
      // que la wallet enseña como "execution reverted" a secas— despues de que el usuario haya
      // firmado y pagado. Una lectura gratis lo convierte en una frase que se entiende.
      //
      // LA LECTURA VA AL BLOQUE EXACTO DEL DEPOSITO (`recDep.blockNumber`) y con reintentos. Con RPC
      // publicos/proxy que balancean varios nodos (Base sobre todo), un `balanceOf` en "latest" justo
      // despues de minar puede caer en un nodo un pelin por detras y devolver 0 aunque el deposito SI
      // entro —falso "No se pudo completar" visto en Base el 2026-09-05—. Fijando la altura, un nodo
      // que aun no tiene ese bloque LANZA (y se reintenta contra otro) en vez de mentir con estado
      // viejo; el recibo ya garantiza que el deposito no revirtio (esperarRecibo lo comprueba).
      let dentro = 0n;
      for (let intento = 0; intento < 6; intento++) {
        try {
          dentro = await publico.readContract({
            address: dep.address, abi: erc20Abi, functionName: "balanceOf", args: [vault],
            blockNumber: recDep.blockNumber,
          });
          if (dentro >= totalWei) break;
        } catch { /* nodo por detras del bloque del deposito: esperar y reintentar contra otro */ }
        await new Promise((r) => setTimeout(r, 1200));
      }
      if (dentro < totalWei) {
        throw new Error(
          `El vault ${vault.slice(0, 8)}… tiene ${formatUnits(dentro, dep.decimals)} ${dep.symbol} ` +
            `dentro y hacen falta ${formatUnits(totalWei, dep.decimals)}. El depósito no llegó a ` +
            `entrar, así que no firmo la apertura: revertiría y te costaría gas para nada. Tu ` +
            `dinero sigue en tu wallet — vuelve a intentarlo.`
        );
      }

      // ---- 7.a LA BOLSA DE GAS EN EL ESTABLE (solo rescate). Va en su propio approve + deposit
      //          porque es OTRO token que el que se trae. Quien viene a rescatar trae el volátil
      //          barato, y pagar el gas con ese volátil sería venderlo al precio malo justo cuando
      //          se viene a no venderlo.
      const estableTotal = Number(pet.gasEnEstable ?? 0) + Number(pet.estableExtra ?? 0);
      if (estableTotal > 0) {
        const est = estableEsToken0 ? pet.pool.token0 : pet.pool.token1;
        // Un solo permiso y un solo depósito para las dos cosas: el capital en estable y la bolsa de
        // gas. Lo que es bolsa se declara aparte al abrir; el resto entra en la banda.
        const estWei = parseUnits(pet.gasEnEstable ?? "0", est.decimals) + parseUnits(pet.estableExtra ?? "0", est.decimals);
        const saldoEst = await publico.readContract({
          address: est.address, abi: erc20Abi, functionName: "balanceOf", args: [address],
        });
        if (saldoEst < estWei) {
          throw new Error(
            `Para la bolsa de gas hacen falta ${formatUnits(estWei, est.decimals)} ${est.symbol} y ` +
              `tienes ${formatUnits(saldoEst, est.decimals)}. Bájala o ponla en ${dep.symbol}.`
          );
        }
        const permisoEst = await publico.readContract({
          address: est.address, abi: erc20Abi, functionName: "allowance", args: [address, vault],
        });
        if (permisoEst < estWei) {
          const hA = await wallet.writeContract({
            address: est.address, abi: erc20Abi, functionName: "approve", args: [vault, estWei],
          });
          await esperarRecibo(config, hA, red.idCadena, "El permiso sobre tu " + est.symbol);
        }
        let gasDepEst: bigint | undefined;
        try {
          const e = await publico.estimateContractGas({
            address: vault, abi: keelVaultAbi, functionName: "deposit", args: [est.address, estWei],
            account: address,
          });
          gasDepEst = (e * 15n) / 10n;
        } catch { /* que lo estime la wallet */ }
        const hD = await wallet.writeContract({
          address: vault, abi: keelVaultAbi, functionName: "deposit", args: [est.address, estWei],
          ...(gasDepEst ? { gas: gasDepEst } : {}),
        });
        await esperarRecibo(config, hD, red.idCadena, "El depósito de la bolsa de gas");
      }

      // ---- 7.b guardias del vault ANTES de abrir: cadencia de reajuste y, si la pool lo pide, el
      //         deslizamiento.
      //
      // Va aqui y no despues por una razon sencilla: `open` es la transaccion que revienta. Firmarla
      // con el valor de fabrica en una pool fina es tirar el gas a sabiendas. Y aprovechamos LA MISMA
      // firma para dejar el `cooldown` corto: el keeper sondea cada 5 min, pero con el cooldown de
      // fabrica (3600 s) una piscina se queda fuera de rango hasta ~55 min esperando la hora minima.
      // Con 300 s rebalancea en cuanto se sale (el sondeo del keeper es el suelo real). El dueño lo
      // puede volver a subir en Ajustes cuando quiera.
      {
        // `setGuards` los pide los cuatro juntos, asi que los que no tocamos se leen y se devuelven
        // tal cual. La desviacion del TWAP SI es una decision de seguridad del dueño —abre la puerta
        // a que alguien empuje el precio— y no se cambia sin que la pida; el `twapWindow` de fabrica
        // (300 s) ya es el del preset, asi que tampoco se toca.
        const [twapHoy, devHoy, slipHoy, cdHoy] = await publico.multicall({
          allowFailure: false,
          contracts: [
            { address: vault, abi: keelVaultAbi, functionName: "twapWindow" },
            { address: vault, abi: keelVaultAbi, functionName: "maxTwapDeviationTicks" },
            { address: vault, abi: keelVaultAbi, functionName: "swapSlippageBps" },
            { address: vault, abi: keelVaultAbi, functionName: "cooldown" },
          ],
        });
        const slipFinal = ajustarSlippage ? bpsNecesarios : Number(slipHoy);
        // Solo se firma si hay algo que cambiar: un vault reutilizado que ya venga con la cadencia
        // corta (y el deslizamiento bueno) no paga una tx de mas. `Number()` porque viem puede
        // devolver estos enteros como bigint o como number segun version.
        if (slipFinal !== Number(slipHoy) || Number(cdHoy) !== NUEVO_COOLDOWN) {
          const hGuard = await wallet.writeContract({
            address: vault,
            abi: keelVaultAbi,
            functionName: "setGuards",
            args: [twapHoy, devHoy, slipFinal, NUEVO_COOLDOWN],
          });
          await esperarRecibo(config, hGuard, red.idCadena, "Los ajustes del vault (cadencia y deslizamiento)");
        }
      }

      // ---- 7.c RESCATE: EL OBJETIVO QUE LLEGA AL CONTRATO ES EL QUE ESCRIBIÓ EL DUEÑO, sin colchón.
      //
      // `_prepareOpen` pone un colchón FIJO de 3 unidades del estable (`DEFAULT_RECOVER_MARGIN_UNITS`)
      // si encuentra `recoverMarginRef == 0` y `recoverMarginBps` en su valor de fábrica (50), y
      // `_recover` apunta a `costBasisRef + recoverMarginRef`. El tope del 5% solo vive en el setter,
      // así que ahí no aplica: el 2026-09-30, 10 USDG con objetivo 12 abrieron contra 15 (+25%), banda
      // ~2× y recortada. `setRecoverMargin(0)` saca al bps de su valor de fábrica y con eso
      // `_prepareOpen` deja el colchón a 0 — `setRecoverMarginRef(0)` NO bastaría, es justo la
      // condición que lo dispara. La bolsa de gas no entra en ninguna de las dos cuentas: va declarada
      // en `openRecovery` (`gasBudget*`) y `_deployable` la aparta del capital.
      // 2026-10-02: aplica a TODA apertura, no solo al rescate: sin colchón, el objetivo es el capital.
      {
        const [margenRef, margenBps] = await publico.multicall({
          allowFailure: false,
          contracts: [
            { address: vault, abi: keelVaultAbi, functionName: "recoverMarginRef" },
            { address: vault, abi: keelVaultAbi, functionName: "recoverMarginBps" },
          ],
        });
        if (Number(margenBps) !== 0) {
          const h = await wallet.writeContract({
            address: vault, abi: keelVaultAbi, functionName: "setRecoverMargin", args: [0],
          });
          await esperarRecibo(config, h, red.idCadena, "Quitar el colchón de fábrica del objetivo");
        }
        if (BigInt(margenRef) !== 0n) {
          const h = await wallet.writeContract({
            address: vault, abi: keelVaultAbi, functionName: "setRecoverMarginRef", args: [0n],
          });
          await esperarRecibo(config, h, red.idCadena, "Quitar el colchón del objetivo");
        }
      }

      // ---- 8. abrir. La bolsa de gas va en el token que se deposita.
      paso = 3;
      marcar(paso, "haciendo");
      // Mismo motivo que en el depósito: la apertura mueve los dos tokens hacia el pool y, con
      // tokens caros, el límite que calcula la wallet antes de tiempo se queda corto. Aquí ya hay
      // recibo del depósito, así que la estimación es contra el estado real.
      let gasOpen: bigint | undefined;
      try {
        const est = await publico.estimateContractGas({
          address: vault, abi: keelVaultAbi, functionName: fnOpen, args: argsOpen as never, account: address,
        });
        gasOpen = (est * 15n) / 10n;
      } catch { /* que estime la wallet, como siempre */ }

      const hOpen = await wallet.writeContract({
        address: vault,
        abi: keelVaultAbi,
        ...(gasOpen ? { gas: gasOpen } : {}),
        // Asimétrico → `openAsym(…, below, above, …)`, que respeta la banda descentrada tal cual y la
        // conserva en cada re-centrado. Simétrico (±%) → el `open` de siempre con un solo semiancho.
        functionName: pet.recuperar ? "openRecovery" : asimetrico ? "openAsym" : "open",
        args: argsRecuperar
          ? argsRecuperar
          : asimetrico
          ? [
              depEsToken0,            // zeroForOne: si depositas token0, se vende token0
              swapWei,
              below,
              above,
              estableEsToken0,        // cual de los dos es la referencia
              reservaWei,
              depEsToken0 ? gasWei : 0n,
              depEsToken0 ? 0n : gasWei,
            ]
          : [
              depEsToken0,
              swapWei,
              below,                  // = above: el semiancho simétrico
              estableEsToken0,
              reservaWei,
              depEsToken0 ? gasWei : 0n,
              depEsToken0 ? 0n : gasWei,
            ],
      });
      await esperarRecibo(config, hOpen, red.idCadena, "La apertura de la posición");
      marcar(paso, "hecho");

      // ---- 8.b MICRO-COLCHÓN = COSTE REAL DE SALIR (decisión del dueño 2026-10-02). Con colchón 0 el
      // contrato compara "valor < capital" sin margen: si en el techo de la banda falta un 0,03% por
      // la comisión de la pool, RECOMPRA el activo y abre otra banda (simulado en fork, keel-beta
      // sim/rebalanceos-sin-colchon-20261002.md). Se fija el colchón a la comisión de la pool + 0,10%
      // de deslizamiento (0,15% en pools del 0,05%, 0,35% en las del 0,25%): alarga la banda solo en
      // esa fracción. En el rescate NO: ahí el objetivo es el que escribe el dueño.
      if (!pet.recuperar) {
        try {
          const [cb, margen, pool] = await publico.multicall({
            allowFailure: false,
            contracts: [
              { address: vault, abi: keelVaultAbi, functionName: "costBasisRef" },
              { address: vault, abi: keelVaultAbi, functionName: "recoverMarginRef" },
              { address: vault, abi: keelVaultAbi, functionName: "pool" },
            ],
          });
          const fee = await publico.readContract({
            address: pool as Address, abi: [{ type: "function", name: "fee", stateMutability: "view", inputs: [], outputs: [{ type: "uint24" }] }],
            functionName: "fee",
          });
          const bps = BigInt(Math.round(Number(fee) / 100)) + 10n;     // fee 500 → 5 bps; + 10 bps de deslizamiento
          const micro = (BigInt(cb) * bps) / 10_000n;
          if (BigInt(margen) !== micro && micro > 0n) {
            const h = await wallet.writeContract({
              address: vault, abi: keelVaultAbi, functionName: "setRecoverMarginRef", args: [micro],
            });
            await esperarRecibo(config, h, red.idCadena, "Ajustar el colchón al coste de salir");
          }
        } catch { /* no es crítico: el vault ya está abierto; el colchón se puede ajustar en su ficha */ }
      }


      // ---- 9. la cuota. Sin esto el robot no toca el vault, pase lo que pase.
      //         Un vault reutilizado suele traerla de la vez anterior: no se cobra dos veces.
      paso = 3;
      if (hazCuota) {
        paso++;
        marcar(paso, "haciendo");
        const hCuota = await wallet.writeContract({
          address: vault, abi: keelVaultAbi, functionName: "addQuota", args: [500],
        });
        await esperarRecibo(config, hCuota, red.idCadena, "La carga de la cuota");
        marcar(paso, "hecho");
      }

      // ---- 10. los ajustes que no vienen solos, y los que hay que DESHACER al reutilizar
      if (hazReanudar) {
        paso++;
        marcar(paso, "haciendo");
        const h = await wallet.writeContract({
          address: vault, abi: keelVaultAbi, functionName: "setPaused", args: [false],
        });
        await esperarRecibo(config, h, red.idCadena, "La reanudación del vault");
        marcar(paso, "hecho");
      }

      if (hazOperador) {
        paso++;
        marcar(paso, "haciendo");
        const h = await wallet.writeContract({
          address: vault, abi: keelVaultAbi, functionName: "setOperator", args: [operadorHoy],
        });
        await esperarRecibo(config, h, red.idCadena, "El cambio de operador");
        marcar(paso, "hecho");
      }

      if (hazReinyeccion) {
        paso++;
        marcar(paso, "haciendo");
        const h = await wallet.writeContract({
          address: vault, abi: keelVaultAbi, functionName: "setReinjection",
          args: [
            pet.modoReinyeccion,
            Math.round(pet.reinyeccionPct * 100),
            Math.round(pet.caidaPct * 100),
          ],
        });
        await esperarRecibo(config, h, red.idCadena, "La reinyección (DCA)");
        marcar(paso, "hecho");
      }

      if (hazReinvertir) {
        paso++;
        marcar(paso, "haciendo");
        const h = await wallet.writeContract({
          address: vault, abi: keelVaultAbi, functionName: "setReinvest", args: [pet.reinvertir],
        });
        await esperarRecibo(config, h, red.idCadena, "La reinversión");
        marcar(paso, "hecho");
      }

      setVaultCreado(vault);
      // La piscina recién nacida tiene que aparecer YA, sin esperar a que caduque la caché: en la
      // lista privada y en la portada pública (`flota`), y con su ganado a cero (`cobrado`). Sin
      // esto, la portada seguía enseñando la caché de hace hasta dos minutos y el dueño no veía su
      // propia piscina — que es exactamente lo que se reportó con la de QQQB en BNB.
      qc.invalidateQueries({ queryKey: ["flota"] });
      qc.invalidateQueries({ queryKey: ["vaults"] });
      qc.invalidateQueries({ queryKey: ["cobrado"] });
    } catch (e) {
      marcar(paso, "fallo");
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  // Descarta el error y los pasos para volver al formulario tras un fallo (el modal de proceso lo usa
  // al cerrar). No toca `vaultCreado`: un éxito tiene su propia pantalla.
  const limpiar = () => { setError(null); setPasos([]); setVaultCreado(null); };

  return { crear, pasos, error, vaultCreado, ocupado, limpiar };
}
